import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { sendFailureNotification } from "shared/toast_notifications"
import { allActionsStore } from "../../../stores/actions/all_actions_store"

/**
 * Supplier controller for all available actions
 *
 * This controller fetches all available actions from the API on page load
 * and populates the allActionsStore for instant access by the permissions modal.
 *
 * Connects to data-controller="suppliers--actions--all-actions-supplier"
 */
export default class extends Controller {

    static stores = [
        allActionsStore
    ]

    connect() {
        useStore(this)

        // Check if data is already loaded
        const currentStore = this.allActionsStoreValue

        if (!currentStore.initialLoadComplete) {
            // The actions data hasn't been loaded yet, fetch it now
            this.fetchAllActions()
        }
    }

    /**
     * Fetch all available actions and populate the store
     *
     * This method:
     * 1. Fetches all actions from /api/actions/list (with pagination if needed)
     * 2. Filters out public actions (implicitly granted)
     * 3. Flattens the grouped structure into a single array
     * 4. Updates the store with the complete actions list
     */
    async fetchAllActions() {
        try {
            const actions = []
            let paginationToken = null

            // Fetch all pages of actions
            do {
                const params = new URLSearchParams()
                if (paginationToken) {
                    params.append('pagination_token', paginationToken)
                }

                const url = '/api/actions/list' + (paginationToken ? `?${params}` : '')
                
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    credentials: 'same-origin',
                    signal: AbortSignal.timeout(30000)  // 30 second timeout
                })

                if (!response.ok) {
                    this.handleApiError(response.status)
                    return
                }

                const data = await response.json()

                // Handle malformed response
                if (!data || typeof data !== 'object') {
                    throw new Error('Malformed API response')
                }

                // Extract actions from the grouped structure
                // API returns: { service: { resource_name: [actions], ... }, description: { ... } }
                // We'll use the 'service' grouping which groups by resource name
                if (data.service) {
                    Object.entries(data.service).forEach(([resourceName, resourceActions]) => {
                        if (Array.isArray(resourceActions)) {
                            resourceActions.forEach(action => {
                                // Filter out public actions (they're implicitly granted)
                                // Note: The API already filters these, but we double-check for safety
                                if (!action.public) {
                                    actions.push({
                                        identifier: action.identifier,
                                        description: action.description,
                                        category: action.category,
                                        public: false
                                    })
                                }
                            })
                        }
                    })
                }

                // Check for pagination token
                paginationToken = data.pagination_token || null
            } while (paginationToken != null)

            // Handle empty result set
            if (actions.length === 0) {
                console.warn('No actions returned from API')
            }

            // Update the store with the complete actions list
            this.setAllActionsStoreValue({
                lastRefreshTime: new Date().toISOString(),
                initialLoadComplete: true,
                actions: actions
            })

        } catch (error) {
            console.error('Error loading actions:', {
                error: error.message,
                name: error.name,
                timestamp: new Date().toISOString()
            })

            // Handle specific error types with user-friendly messages
            let errorMessage = 'Unable to load actions information. Please refresh the page.'
            
            if (error.name === 'TimeoutError' || error.name === 'AbortError') {
                errorMessage = 'Request timed out while loading actions. Please refresh the page.'
            } else if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
                errorMessage = 'Unable to connect to server. Please check your connection and refresh the page.'
            } else if (error.message === 'Malformed API response') {
                errorMessage = 'Received invalid data from server. Please refresh the page.'
            }

            // Show user-friendly error message
            sendFailureNotification(errorMessage)

            // Keep initialLoadComplete as false on error
            // This allows the modal to show a loading state or retry
        }
    }

    /**
     * Handle API errors based on status code
     * @param {number} status - HTTP status code
     */
    handleApiError(status) {
        let message = ''
        
        switch (status) {
            case 401:
            case 403:
                message = "You don't have permission to view actions data."
                break
            case 404:
                message = "Actions endpoint not found."
                break
            case 408:
            case 504:
                message = "Request timed out while loading actions. Please refresh the page."
                break
            case 500:
            case 502:
            case 503:
                message = "Server error occurred while loading actions. Please refresh the page."
                break
            default:
                message = "An error occurred while loading actions. Please refresh the page."
        }
        
        console.error('API Error:', {
            status,
            message,
            timestamp: new Date().toISOString()
        })
        
        sendFailureNotification(message)
        throw new Error(`HTTP error! status: ${status}`)
    }

    /**
     * Manually refresh the actions list
     * (Can be called by other controllers if needed)
     */
    refreshActions() {
        this.fetchAllActions()
    }
}
